// Banner.tsx
import React, { useState } from "react";
import styles from "./Banner.module.scss";
import { getImageUri } from "../../../../common/utils/functions/getImageUri";
import { useBanner } from "./useBanner";
import { getBannerVariant } from "../../../../config/runtimeConfig";

interface BaseBannerItem {
  id?: number | string;
  name: string;
  description?: string;
  files: string[];
  discount?: number;
  showText?: boolean;
}

interface BannerProps<T extends BaseBannerItem> {
  items: T[];
  onItemPress?: (item: T) => void;
  renderContent?: (item: T) => React.ReactNode;
  noText?: boolean;
  variantProp?: "base" | "split" | "expanded" | "compact" | "hero";
}

export function Banner<T extends BaseBannerItem>({
  items,
  onItemPress,
  renderContent,
  noText = false,
  variantProp,
}: BannerProps<T>) {
  const variant =
    variantProp ||
    (getBannerVariant() as "base" | "split" | "expanded" | "compact" | "hero");
  const {
    emblaRef,
    selectedIndex,
    getVariantClass,
    handleImageLoad,
    handlePrev,
    handleNext,
    handleDotClick,
    isImageLoaded,
    hasCarousel,
    registerVideoRef,
    isVideo,
    getMediaFile,
    getOverlayImage,
    hasVideoAndImage,
  } = useBanner({ items, variant });

  const [videoReadyMap, setVideoReadyMap] = useState<
    Record<string | number, boolean>
  >({});

  if (!items?.length) return null;

  const renderMedia = (item: T, index: number) => {
    const mediaFile = getMediaFile(item.files || []);
    if (!mediaFile) return null;

    const imageUrl = `${getImageUri()}${mediaFile}`;
    const isVideoFile = isVideo(mediaFile);
    const overlayImage = getOverlayImage(item.files || []);
    const hasBoth = hasVideoAndImage(item.files || []);
    const isHero = variant === "hero";
    const itemId = item.id || index;
    const isVideoReady = videoReadyMap[itemId] || false;

    // Hero вариант: видео + фото
    if (isHero && hasBoth && isVideoFile && overlayImage) {
      const posterFile = item.files?.find((f: string) => f.includes("poster_"));
      const posterUrl = posterFile
        ? `${getImageUri()}${posterFile}`
        : undefined;

      return (
        <div className={styles.videoWithOverlay}>
          {/* Постер на весь блок, пока видео не загрузилось */}
          {!isVideoReady && posterUrl && (
            <img
              src={posterUrl}
              className={styles.videoBackground}
              alt="загрузка..."
            />
          )}

          <video
            ref={(el) => registerVideoRef(index, el)}
            className={`${styles.videoBackground} ${!isVideoReady ? styles.videoHidden : ""}`}
            src={imageUrl}
            poster={posterUrl}
            muted
            playsInline
            loop
            onLoadedData={() => {
              console.log("🎬 Video loaded for item:", itemId);
              setVideoReadyMap((prev) => ({ ...prev, [itemId]: true }));
              handleImageLoad(item.id);
            }}
          />

          {/* Фото-оверлей 300×450 по центру */}
          <img
            className={styles.overlayImage}
            src={`${getImageUri()}${overlayImage}`}
            alt={item.name}
          />
        </div>
      );
    }

    // Обычный режим: только видео или только фото
    if (isVideoFile) {
      const posterFile = item.files?.find((f: string) => f.includes("poster_"));
      const posterUrl = posterFile
        ? `${getImageUri()}${posterFile}`
        : undefined;

      return (
        <video
          ref={(el) => registerVideoRef(index, el)}
          className={styles.image}
          src={imageUrl}
          poster={posterUrl}
          muted
          playsInline
          loop
          onLoadedData={() => handleImageLoad(item.id)}
        />
      );
    }

    return (
      <img
        className={styles.image}
        src={imageUrl}
        alt={item.name}
        onLoad={() => handleImageLoad(item.id)}
      />
    );
  };

  return (
    <div className={`${styles.banner} ${styles[getVariantClass()]}`}>
      <div className={styles.viewport} ref={emblaRef}>
        <div className={styles.container}>
          {items.map((item, index) => {
            const imageLoaded = isImageLoaded(item.id);
            const shouldShowText = item.showText !== false && !noText;
            const mediaFile = getMediaFile(item.files || []);
            const isVideoFile = isVideo(mediaFile);
            const overlayImage = getOverlayImage(item.files || []);
            const hasBoth = hasVideoAndImage(item.files || []);
            const isHero = variant === "hero";
            const showOverlay =
              isHero && hasBoth && isVideoFile && overlayImage;

            return (
              <div
                key={item.id}
                className={styles.slide}
                onClick={() => onItemPress?.(item)}
              >
                {renderContent ? (
                  renderContent(item)
                ) : (
                  <>
                    <div className={styles.imageContainer}>
                      {mediaFile && (
                        <>
                          {renderMedia(item, index)}
                          {!imageLoaded && (
                            <div className={styles.imagePlaceholder} />
                          )}
                        </>
                      )}
                    </div>

                    {imageLoaded && shouldShowText && (
                      <div
                        className={
                          showOverlay ? styles.contentOverlay : styles.content
                        }
                      >
                        <h3 className={styles.title}>{item.name}</h3>
                        {item.description && (
                          <p className={styles.description}>
                            {item.description}
                          </p>
                        )}
                      </div>
                    )}

                    {item.discount && imageLoaded && (
                      <div className={styles.discountBadge}>
                        <span className={styles.discountBadgeText}>
                          -{item.discount}%
                        </span>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {hasCarousel && (
        <>
          <button className={styles.arrowPrev} onClick={handlePrev}>
            ‹
          </button>
          <button className={styles.arrowNext} onClick={handleNext}>
            ›
          </button>

          <div className={styles.dots}>
            {items.map((item, index) => (
              <button
                key={item.id}
                className={`${styles.dot} ${
                  index === selectedIndex ? styles.dotActive : ""
                }`}
                onClick={() => handleDotClick(index)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
